<script setup>
import { computed } from 'vue'

/**
 * Tactical L-shaped corner brackets overlaid on a positioned parent. Used
 * across the brand for the "tactical viewport" feel - home map card,
 * locations active card, profile cover hero.
 *
 * The parent element MUST have `position: relative` (or any non-static
 * position) - this component absolutely positions itself inset-0 and
 * places its bracket spans at the four (or selected) corners.
 *
 * Variants control size + corner subset + entrance animation:
 *   - `hero`  → 22 px L-shapes, 14 px inset, all 4 corners, static
 *   - `card`  → 12 px L-shapes,  5 px inset, TR + BR corners, scale-in
 *               animation (used as a "selection lock-on" indicator)
 *
 * For finer control, pass `corners` explicitly: `['tl', 'tr', 'bl', 'br']`.
 *
 * Examples:
 *   <CornerBrackets variant="hero" />                            <!-- 4-corner static -->
 *   <CornerBrackets variant="card" />                            <!-- 2-corner animated -->
 *   <CornerBrackets variant="hero" :corners="['tr', 'br']" />    <!-- mix-and-match -->
 */
const props = defineProps({
    variant: {
        type: String,
        default: 'hero',
        validator: (v) => ['hero', 'card'].includes(v),
    },
    /**
     * Override which corners to render. Falls back to the variant's default
     * corner set when not supplied.
     */
    corners: {
        type: Array,
        default: null,
        validator: (arr) => arr.every((c) => ['tl', 'tr', 'bl', 'br'].includes(c)),
    },
    /**
     * Force entrance animation on/off regardless of variant default. Useful
     * if you want a hero variant to fade in when its parent becomes active.
     */
    animated: {
        type: Boolean,
        default: null,
    },
})

const VARIANT_DEFAULTS = {
    hero: { corners: ['tl', 'tr', 'bl', 'br'], animated: false },
    card: { corners: ['tr', 'br'], animated: true },
}

const activeCorners = computed(() => props.corners ?? VARIANT_DEFAULTS[props.variant].corners)
const isAnimated = computed(() => props.animated ?? VARIANT_DEFAULTS[props.variant].animated)
</script>

<template>
    <div
        class="pointer-events-none absolute inset-0"
        aria-hidden="true"
    >
        <span
            v-for="corner in activeCorners"
            :key="corner"
            :class="[
                'ridesnap-bracket',
                `ridesnap-bracket--${variant}`,
                `ridesnap-bracket--${corner}`,
                isAnimated ? 'ridesnap-bracket--animated' : '',
            ]"
        ></span>
    </div>
</template>

<style>
/* Non-scoped so the keyframe + class names stay stable for the parent's
 * inspection (parent's other `aria-hidden` corner decorations can coexist).
 * The base bracket - orange L-shape via two transparent border edges. */
.ridesnap-bracket {
    position: absolute;
    border: 2px solid rgba(249, 115, 22, 0.55);
    pointer-events: none;
}

.ridesnap-bracket--hero {
    width: 22px;
    height: 22px;
}

.ridesnap-bracket--card {
    width: 12px;
    height: 12px;
}

.ridesnap-bracket--hero.ridesnap-bracket--tl {
    top: 14px;
    left: 14px;
    border-right: none;
    border-bottom: none;
}
.ridesnap-bracket--hero.ridesnap-bracket--tr {
    top: 14px;
    right: 14px;
    border-left: none;
    border-bottom: none;
}
.ridesnap-bracket--hero.ridesnap-bracket--bl {
    bottom: 14px;
    left: 14px;
    border-right: none;
    border-top: none;
}
.ridesnap-bracket--hero.ridesnap-bracket--br {
    bottom: 14px;
    right: 14px;
    border-left: none;
    border-top: none;
}

.ridesnap-bracket--card.ridesnap-bracket--tl {
    top: 5px;
    left: 5px;
    border-right: none;
    border-bottom: none;
}
.ridesnap-bracket--card.ridesnap-bracket--tr {
    top: 5px;
    right: 5px;
    border-left: none;
    border-bottom: none;
}
.ridesnap-bracket--card.ridesnap-bracket--bl {
    bottom: 5px;
    left: 5px;
    border-right: none;
    border-top: none;
}
.ridesnap-bracket--card.ridesnap-bracket--br {
    bottom: 5px;
    right: 5px;
    border-left: none;
    border-top: none;
}

/* "Lock-on" entrance - scale from 40 % with fade-in, used by the
 * locations-active card to acknowledge the user's hover/select. */
.ridesnap-bracket--animated {
    animation: ridesnap-bracket-in 0.22s ease-out forwards;
}

@keyframes ridesnap-bracket-in {
    from {
        opacity: 0;
        transform: scale(0.4);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@media (prefers-reduced-motion: reduce) {
    .ridesnap-bracket--animated {
        animation: none;
        opacity: 1;
    }
}
</style>
